import React, { useEffect, useState } from "react";
import {
    Star as StarIcon,
    Loader2,
    Save,
    Plus,
    Trash2,
    Globe,
    GripVertical,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Switch } from "../../components/ui/switch";

const LANGS = ["tr", "en", "de"];

export default function AdminStarRequirements() {
    const [activeStar, setActiveStar] = useState(3);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [activeLang, setActiveLang] = useState("tr");

    const load = async (star) => {
        setLoading(true);
        try {
            const { data: doc } = await api.get(`/admin/content/star-requirements/${star}`);
            setData(doc);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(activeStar); }, [activeStar]);

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/admin/content/star-requirements/${activeStar}`, {
                star_level: activeStar,
                general_description: data.general_description || {},
                categories: data.categories || [],
                seo: data.seo || {},
            });
            toast.success("Kaydedildi.");
            await load(activeStar);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const updateRoot = (key, value) => setData((p) => ({ ...p, [key]: value }));
    const updateLang = (key, value) =>
        setData((p) => ({ ...p, [key]: { ...(p[key] || {}), [activeLang]: value } }));

    const updateCategory = (ci, fn) => {
        setData((p) => {
            const next = JSON.parse(JSON.stringify(p));
            next.categories = next.categories || [];
            next.categories[ci] = fn(next.categories[ci]);
            return next;
        });
    };

    const addCategory = () => {
        setData((p) => ({
            ...p,
            categories: [
                ...(p.categories || []),
                { name: { tr: "Yeni Kategori", en: "", de: "" }, requirements: [] },
            ],
        }));
    };

    const removeCategory = (ci) => {
        if (!window.confirm("Kategori ve içindeki tüm gereksinimler silinecek. Onaylıyor musunuz?")) return;
        setData((p) => ({
            ...p,
            categories: (p.categories || []).filter((_, i) => i !== ci),
        }));
    };

    const addReq = (ci) => {
        updateCategory(ci, (cat) => ({
            ...cat,
            requirements: [
                ...(cat.requirements || []),
                { text: { tr: "", en: "", de: "" }, is_mandatory: true, source_reference: "", sort_order: (cat.requirements?.length || 0) + 1 },
            ],
        }));
    };

    const removeReq = (ci, ri) => {
        updateCategory(ci, (cat) => ({
            ...cat,
            requirements: (cat.requirements || []).filter((_, i) => i !== ri),
        }));
    };

    const updateReq = (ci, ri, key, value) => {
        updateCategory(ci, (cat) => {
            const reqs = [...(cat.requirements || [])];
            reqs[ri] = { ...reqs[ri], [key]: value };
            return { ...cat, requirements: reqs };
        });
    };

    const updateReqLang = (ci, ri, value) => {
        updateCategory(ci, (cat) => {
            const reqs = [...(cat.requirements || [])];
            reqs[ri] = { ...reqs[ri], text: { ...(reqs[ri].text || {}), [activeLang]: value } };
            return { ...cat, requirements: reqs };
        });
    };

    return (
        <div data-testid="admin-stars-page">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
                <div>
                    <h1 className="text-2xl font-bold text-dark-navy inline-flex items-center gap-2">
                        <StarIcon className="w-6 h-6 text-teal" /> Yıldız Standartları
                    </h1>
                    <p className="text-sm text-deep-blue/70 mt-1">
                        T.C. Kültür ve Turizm Bakanlığı yönetmeliği bazlı kategori ve kriter veritabanı (TR/EN/DE).
                    </p>
                </div>
                <button
                    onClick={save}
                    disabled={saving || loading}
                    className="bg-teal hover:bg-teal-700 text-dark-navy text-sm font-bold px-5 py-2 rounded-md inline-flex items-center gap-2 disabled:opacity-60"
                    data-testid="stars-save"
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Kaydet
                </button>
            </div>

            <div className="flex items-center gap-2 mb-4 flex-wrap">
                {[1, 2, 3, 4, 5].map((s) => (
                    <button
                        key={s}
                        onClick={() => setActiveStar(s)}
                        data-testid={`stars-tab-${s}`}
                        className={`px-4 py-3 rounded-md text-sm font-bold transition ${activeStar === s ? "bg-dark-navy text-white" : "bg-white text-deep-blue border border-slate-200 hover:border-gold/40"}`}
                    >
                        <span className="font-data text-gold mr-2">{Array.from({ length: s }).map(() => "★").join("")}</span>
                        {s} Yıldız
                    </button>
                ))}
            </div>

            <div className="flex items-center gap-1 border-b border-slate-200 mb-5">
                {LANGS.map((l) => (
                    <button
                        key={l}
                        onClick={() => setActiveLang(l)}
                        className={`px-4 py-2 text-xs font-bold uppercase tracking-wider ${activeLang === l ? "border-b-2 border-teal text-teal-700" : "text-deep-blue/70"}`}
                        data-testid={`stars-lang-${l}`}
                    >
                        <Globe className="w-3 h-3 inline mr-1" /> {l}
                    </button>
                ))}
            </div>

            {loading || !data ? (
                <div className="text-center text-deep-blue/60 text-sm py-12 inline-flex items-center justify-center gap-2 w-full">
                    <Loader2 className="w-5 h-5 animate-spin" /> Yükleniyor...
                </div>
            ) : (
                <div className="space-y-4">
                    {/* General description */}
                    <div className="bg-white rounded-lg shadow-md border border-slate-100 p-5">
                        <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80">Genel Açıklama ({activeLang})</label>
                        <textarea
                            rows={3}
                            value={(data.general_description || {})[activeLang] || ""}
                            onChange={(e) => updateLang("general_description", e.target.value)}
                            className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm"
                            placeholder={`${activeStar} yıldız oteller için genel açıklama...`}
                            data-testid="stars-general-desc"
                        />
                        <div className="mt-4 grid sm:grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80">SEO Meta Title</label>
                                <input
                                    value={data.seo?.meta_title || ""}
                                    onChange={(e) => updateRoot("seo", { ...(data.seo || {}), meta_title: e.target.value })}
                                    className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80">SEO Meta Description</label>
                                <input
                                    value={data.seo?.meta_desc || ""}
                                    onChange={(e) => updateRoot("seo", { ...(data.seo || {}), meta_desc: e.target.value })}
                                    className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Categories */}
                    {(data.categories || []).map((cat, ci) => (
                        <div key={ci} className="bg-white rounded-lg shadow-md border border-slate-100 p-5" data-testid={`stars-cat-${ci}`}>
                            <div className="flex items-start gap-3 mb-4">
                                <GripVertical className="w-4 h-4 text-deep-blue/30 mt-2 shrink-0" />
                                <input
                                    placeholder={`Kategori adı (${activeLang})`}
                                    value={(cat.name || {})[activeLang] || ""}
                                    onChange={(e) => updateCategory(ci, (c) => ({ ...c, name: { ...(c.name || {}), [activeLang]: e.target.value } }))}
                                    className="flex-1 px-3 py-2 rounded-md border border-slate-300 text-base font-bold"
                                    data-testid={`stars-cat-name-${ci}`}
                                />
                                <button onClick={() => removeCategory(ci)} title="Kategoriyi Sil" className="p-2 rounded-md hover:bg-data-red/10 text-data-red shrink-0">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>

                            <div className="space-y-2">
                                {(cat.requirements || []).map((r, ri) => (
                                    <div key={ri} className="bg-light-bg rounded-md p-3 border border-slate-100 flex items-start gap-3" data-testid={`stars-req-${ci}-${ri}`}>
                                        <div className="flex flex-col items-center gap-1 shrink-0 w-12">
                                            <input
                                                type="number"
                                                value={r.sort_order ?? 0}
                                                onChange={(e) => updateReq(ci, ri, "sort_order", parseInt(e.target.value, 10) || 0)}
                                                className="w-full px-1 py-1 rounded border border-slate-300 text-xs text-center font-data"
                                            />
                                            <Switch
                                                checked={r.is_mandatory !== false}
                                                onCheckedChange={(v) => updateReq(ci, ri, "is_mandatory", v)}
                                            />
                                            <span className="text-[9px] text-deep-blue/70 font-semibold uppercase">{r.is_mandatory !== false ? "Zorun" : "Opsiy"}</span>
                                        </div>
                                        <div className="flex-1 space-y-2">
                                            <textarea
                                                rows={2}
                                                placeholder={`Gereksinim metni (${activeLang})`}
                                                value={(r.text || {})[activeLang] || ""}
                                                onChange={(e) => updateReqLang(ci, ri, e.target.value)}
                                                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm"
                                                data-testid={`stars-req-text-${ci}-${ri}`}
                                            />
                                            <input
                                                placeholder="Kaynak referansı (örn: Madde 4/2)"
                                                value={r.source_reference || ""}
                                                onChange={(e) => updateReq(ci, ri, "source_reference", e.target.value)}
                                                className="w-full px-3 py-2 rounded-md border border-slate-300 text-xs font-mono"
                                            />
                                        </div>
                                        <button onClick={() => removeReq(ci, ri)} className="p-1.5 rounded-md hover:bg-data-red/10 text-data-red shrink-0" title="Gereksinimi Sil">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                                <button
                                    onClick={() => addReq(ci)}
                                    className="w-full py-2 rounded-md border border-dashed border-slate-300 text-deep-blue/70 hover:bg-light-bg text-sm font-semibold inline-flex items-center justify-center gap-1"
                                    data-testid={`stars-add-req-${ci}`}
                                >
                                    <Plus className="w-4 h-4" /> Gereksinim Ekle
                                </button>
                            </div>
                        </div>
                    ))}

                    <button
                        onClick={addCategory}
                        className="w-full py-3 rounded-md border-2 border-dashed border-teal text-teal-700 hover:bg-teal/5 text-sm font-bold inline-flex items-center justify-center gap-2"
                        data-testid="stars-add-cat"
                    >
                        <Plus className="w-4 h-4" /> Yeni Kategori Ekle
                    </button>
                </div>
            )}
        </div>
    );
}
